// @flow

import React, { Component } from 'react';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import SearchInput from 'app/common/react/components/profile/ProfileSearch/SearchInput';
import Testable from 'app/common/react/ui/Testable';
import { normalizeSearchQuery } from 'app/common/react/components/discover/lib/searchQueryUtils';
import { track } from 'app/common/lib/react/analytics';
import { Box, Icon, IconButton } from 'gestalt';

const SEARCHBOX_HEIGHT = 40;

type Props = {|
  appliedQuery: string,
  handleSearchSubmit: (string) => void,
  handleSearchTextChange?: (string) => void,
  inputFocused?: boolean,
|};

type State = {|
  // What has the user entered? This may differ from appliedQuery
  // if the user has changed values but not yet submitted.
  enteredQuery: string,
  inputFocused: boolean,
|};

export class SearchBox extends Component<Props, State> {
  state = {
    enteredQuery: this.props.appliedQuery,
    inputFocused: this.props.inputFocused !== undefined ? this.props.inputFocused : true,
  };

  // -------------------- UI EVENT HANDLERS -------------------- //

  handleInputBlur = () => {
    this.setState({ inputFocused: false });
  };

  handleInputFocus = (event?: SyntheticKeyboardEvent<HTMLInputElement>) => {
    this.setState({ inputFocused: true });

    // log the click into the search input box
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.USER_STOREFRONT,
      element: PCONST.thrift.ElementType.SEARCH_BOX_TEXT_INPUT,
    });
  };

  // -------------------- SEARCH CHANGE EVENT HANDLERS -------------------- //
  handleKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === CONST.KeyCode.ENTER) {
      // enter key down => submit
      this.handleTypedSearchSubmit();
    }
  };

  handleTextChange = (query: string) => {
    this.setState({ enteredQuery: query });

    const normalizedQuery = normalizeSearchQuery(query);
    if (this.props.handleSearchTextChange) {
      this.props.handleSearchTextChange(normalizedQuery);
    }
  };

  handleClearSearch = () => {
    this.setState({
      enteredQuery: '',
      inputFocused: true,
    });
    if (this.props.handleSearchTextChange) {
      this.props.handleSearchTextChange('');
    }
  };

  handleTypedSearchSubmit = () => {
    const { appliedQuery } = this.props;
    const { enteredQuery } = this.state;

    const query = normalizeSearchQuery(enteredQuery);
    if (!query.length || query === appliedQuery) {
      return;
    }

    this.props.handleSearchSubmit(query);
  };

  render() {
    const { inputFocused, enteredQuery } = this.state;

    return (
      <Box flex="grow" role="combobox">
        <Box color="lightGray" display="flex" height={SEARCHBOX_HEIGHT} shape="rounded">
          <Box alignItems="center" display="flex" flex="grow" height="100%" paddingX={4}>
            <Box marginRight={3} flex="none">
              <Icon
                accessibilityLabel={i18n._('Search icon', 'Search icon in search box')}
                icon="search"
                size={20}
              />
            </Box>
            <SearchInput
              isFocused={inputFocused}
              onInputBlur={this.handleInputBlur}
              onInputFocus={this.handleInputFocus}
              onKeyDown={this.handleKeyDown}
              onSearchTrigger={this.handleTypedSearchSubmit}
              onTextChange={this.handleTextChange}
              placeholder={i18n._('Search products')}
              textValue={enteredQuery}
            />
            {enteredQuery && (
              <Box
                marginLeft={3}
                flex="none"
                dangerouslySetInlineStyle={{ __style: { zIndex: 3 } }}
              >
                <Testable id="search-cancel">
                  <IconButton
                    accessibilityLabel={i18n._(
                      'Remove search input',
                      'Accessibility label to remove the search input',
                    )}
                    onClick={this.handleClearSearch}
                    icon="cancel"
                    size="xs"
                  />
                </Testable>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
}

export default SearchBox;
